/** Signed guest WebSocket session tokens (HMAC-SHA256, Web Crypto only). A guest has no Firebase identity, so the ChatRoom WS upgrade cannot verify a Firebase ID token; instead the guest-chat-create route issues a short-lived signed token binding {gid, cid}, and the WS upgrade verifies it (constant-time signature check + expiry + gid/cid match) before allowing the socket. The static API token is NOT accepted as WS auth here. Wire format: base64url(payloadJson) + "." + base64url(HMAC_SHA256(payloadBytes, secret)), payload = { gid, cid, iat, exp } in unix seconds. Workers-native: `crypto.subtle` / `atob` / `btoa` only. */

import { nowEpochSeconds } from '@federa/const/time-units';
import {
	base64UrlToBytes,
	encodeSignedToken,
	parseSignedTokenPayload,
	unexpiredPayloadOrNull,
	verifySignedTokenPayload,
} from '@federa/const/signed-token-codec';
import type { GuestSessionTokenPayload } from '@federa/types/sessions-guest-session-token';

/** The SUBJECT a guest session token binds: the guest user id plus the ONE conversation it is scoped to. Named once
 * because every reader below returns exactly this pair and nothing else -- a token authorizes a guest AT a conversation,
 * never a guest globally, so `cid` is part of the subject rather than a payload field. `| null` at each reader is that
 * reader's own fail-closed arm and stays spelled at the signature, not folded in here. */
type GuestSubject = { gid: string; cid: string };

/** Sign a guest session token binding guest user id + conversation id. `secret` is the HMAC key (from the GUEST_SESSION_SECRET binding); `ttlSec` sets exp = iat + ttlSec. Returns the `base64url(payload).base64url(signature)` string. */
export async function signGuestSessionToken(claims: GuestSubject, secret: string, ttlSec: number): Promise<string> {
	const iat = nowEpochSeconds();
	const payload: GuestSessionTokenPayload = { gid: claims.gid, cid: claims.cid, iat, exp: iat + ttlSec };
	return encodeSignedToken(payload, secret);
}

/**
 * True when `credential` has the WIRE SHAPE of a guest session token -- exactly two dot-separated parts. It is the
 * CLASSIFIER for a surface that accepts either credential kind, and it separates cleanly because a Firebase ID token is
 * a three-part JWT. Callers classify with this and then DENY on a failed verify, rather than falling through to the
 * other verifier: a token that claims to be a guest token and fails its own signature check is a forgery, not a JWT.
 */
export function isGuestSessionTokenShape(credential: string): boolean {
	return typeof credential === 'string' && credential.split('.').length === 2;
}

/** Verify a guest session token: `{ gid, cid }` when the signature is valid AND unexpired; `null` on ANY failure (fail-closed) -- malformed shape, bad signature, expired, or missing/blank claims. */
export async function verifyGuestSessionToken(token: string, secret: string): Promise<GuestSubject | null> {
	// The WIRE-SHAPE classifier runs first and is this family's own: a three-part JWT must not even reach the verify.
	if (!isGuestSessionTokenShape(token)) return null;

	// `verifySignedTokenPayload` is the shared PRE-CLAIMS half (`const/signed-token-codec.ts`): blank token, blank secret
	// (an empty HMAC key verifies NOTHING, so an unconfigured deployment must deny), bad signature, malformed JSON and a
	// non-object body all land on `null`. It is DELIBERATELY not an `as GuestSessionTokenPayload` cast: the bytes are
	// external even after a signature check -- the signature proves who wrote them, never that they carry four members of
	// the right types -- so the claim readers below narrow each member on its own. This prelude and
	// `utils/security/app-session-token.ts`'s were hand-written twins that had already diverged once over exactly that
	// cast; sharing one body is what retires the parity gap `QUAL-UNSAFE-INGRESS-001` reported on both.
	return validateGuestClaims(await verifySignedTokenPayload(token, secret));
}

/** Non-blank gid/cid off a parsed payload, WITHOUT an expiry check -- the unverified reader labels, never authorizes.
 *  Reads off the runtime-narrowed record, so every member is `unknown` until these tests prove otherwise. */
function readGidCid(payload: Record<string, unknown> | null): GuestSubject | null {
	if (!payload) return null;
	const { gid, cid } = payload;
	if (typeof gid !== 'string' || !gid) return null;
	if (typeof cid !== 'string' || !cid) return null;
	return { gid, cid };
}

/** Fail-closed claims check: unexpired + non-blank gid/cid. `unexpiredPayloadOrNull` reads `exp` as `unknown` and answers
 *  null for a missing or non-numeric one, so the un-narrowed member is the gate's own input rather than a hole. */
function validateGuestClaims(payload: Record<string, unknown> | null): GuestSubject | null {
	return readGidCid(unexpiredPayloadOrNull(payload));
}

/**
 * The `{gid, cid}` a token CLAIMS, read without verifying its signature -- for the BROWSER, which holds no secret and
 * so cannot verify, but must know which subject the credential it is about to present will bind to.
 *
 * SECURITY: unverified by construction, so this is NEVER an authorization input. It is safe for its one use -- keeping
 * the `userId` a client sends alongside a token identical to that token's own `gid` -- because a forged or edited
 * payload changes only which subject the CLIENT names, and `verifyGuestSessionToken` on the worker still rejects it.
 * A caller cannot reach anyone else's data by lying here; it can only mislabel itself into its own 401.
 *
 * Null on any malformed input, which callers treat as "no usable subject" and fall back to their own id.
 */
export function readGuestSessionTokenClaimsUnverified(token: string): GuestSubject | null {
	const [encodedPayload] = token.split('.');
	if (!encodedPayload) return null;
	const bytes = base64UrlToBytes(encodedPayload);
	if (bytes.length === 0) return null;
	// parseSignedTokenPayload absorbs non-JSON (null) and readGidCid's field checks absorb non-object JSON -- both land on the fail-closed null this function's contract promises.
	return readGidCid(parseSignedTokenPayload(bytes));
}
